import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MarketNewsCard = ({ 
  title, 
  summary, 
  source, 
  publishedAt, 
  imageUrl, 
  category 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getCategoryColor = (category) => {
    switch (category) {
      case 'market': return 'text-primary';
      case 'earnings': return 'text-success';
      case 'analysis': return 'text-secondary';
      default: return 'text-muted-foreground';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'market': return 'TrendingUp';
      case 'earnings': return 'DollarSign';
      case 'analysis': return 'FileText';
      default: return 'Newspaper';
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}h ago`;
    } else {
      return `${Math.floor(diffInMinutes / 1440)}d ago`;
    }
  };

  const handleReadMore = () => {
    setIsExpanded(!isExpanded);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: title,
        text: summary,
        url: window.location.href
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(`${title}\n\n${summary}`);
      alert('News article copied to clipboard!');
    }
  };

  const handleOpenSource = () => {
    // In a real app, this would open the actual article URL
    alert(`This would open the full article from ${source}. For demo purposes, this is a mock news item.`);
  };

  return (
    <div className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start space-x-4">
        {imageUrl && (
          <div className="flex-shrink-0">
            <img 
              src={imageUrl} 
              alt={title}
              className="w-16 h-16 object-cover rounded-lg"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-2">
            <div className={`flex items-center space-x-1 ${getCategoryColor(category)}`}>
              <Icon name={getCategoryIcon(category)} size={12} />
              <span className="text-xs font-medium capitalize">{category}</span>
            </div>
            <div className="w-1 h-1 bg-muted-foreground rounded-full" />
            <span className="text-xs text-muted-foreground">{source}</span>
            <div className="w-1 h-1 bg-muted-foreground rounded-full" />
            <span className="text-xs text-muted-foreground">
              {formatTimeAgo(publishedAt)}
            </span>
          </div>
          
          <h4 className="text-sm font-semibold text-foreground mb-2 line-clamp-2">
            {title}
          </h4>
          
          <p className={`text-xs text-muted-foreground ${isExpanded ? '' : 'line-clamp-2'}`}>
            {summary}
          </p>
          
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReadMore}
                className="text-xs h-6"
              >
                {isExpanded ? 'Show Less' : 'Read More'}
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleOpenSource}
                className="text-xs h-6"
              >
                <Icon name="ExternalLink" size={12} className="mr-1" />
                Source
              </Button>
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={handleShare}
              className="h-6 w-6"
            >
              <Icon name="Share2" size={12} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketNewsCard;